import { useEffect } from 'react';
import clsx from 'clsx';
import styles from './ReconTable.module.scss';
import LoadingSpinner from '../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import ReconCellContent, { cellBandClass } from './ReconCellContent';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { ensureDrillDown } from '../../../../../store/reconciliations/reconciliationsSlice';
import { selectDrillDownEntry } from '../../../../../store/reconciliations/selectors';
import { useUnits } from '../UnitsContext';
import { isDrillDownReferenceColumn } from '../constants/reconciliationsColumnRules';
import {
    DRILL_DOWN_FETCH_FAILED,
    DRILL_DOWN_NOTES,
    DRILL_DOWN_UNAVAILABLE,
    RECONCILIATIONS_LOADING,
} from '../constants/reconciliationsCopy';
import type { ReconColumn } from '../../../../../store/reconciliations/types';

interface DrillDownRowsProps {
    orgId: string;
    fys: number[] | null;
    reconId: string;
    fy: number;
    columns: ReconColumn[];
}

/** Mounted only while its parent FY row is expanded — fetched once per (reconId, fy) and never
 *  re-fetched on re-expand: the reducer's claim leaves a `success` entry untouched. */
function DrillDownRows({ orgId, fys, reconId, fy, columns }: Readonly<DrillDownRowsProps>) {
    const dispatch = useAppDispatch();
    const entry = useAppSelector((state) => selectDrillDownEntry(state, orgId, fys, reconId, fy));
    const { unit } = useUnits();

    useEffect(() => {
        dispatch(ensureDrillDown({ orgId, fys, reconId, fy }));
    }, [dispatch, orgId, fys, reconId, fy]);

    if (entry?.status === 'loading' || !entry) {
        return (
            <tr className={styles.DrillDownRow}>
                <td colSpan={columns.length + 1} className={styles.DrillDownLoading}>
                    <div role="status" aria-live="polite">
                        <LoadingSpinner height="1.25rem" color="var(--primary-text-colour)" style={{ margin: 0 }} />
                        <span className={styles.SrOnly}>{RECONCILIATIONS_LOADING}</span>
                    </div>
                </td>
            </tr>
        );
    }

    // A failed fetch and a genuinely empty breakup are different claims — the saga already keeps
    // them apart (fetchDrillDownFailed), and presenting lost evidence as a finding about the
    // borrower is the most damaging way this drawer can be wrong.
    if (entry.status === 'error' || !entry.rows?.length) {
        return (
            <tr className={styles.DrillDownRow}>
                <td colSpan={columns.length + 1} className={styles.DrillDownEmpty}>
                    {entry.status === 'error' ? DRILL_DOWN_FETCH_FAILED : DRILL_DOWN_UNAVAILABLE}
                </td>
            </tr>
        );
    }

    const note = DRILL_DOWN_NOTES[reconId];

    // Server order is load-bearing: MATCHED, then GST_ONLY, then BANK_ONLY, then
    // BANK_UNKEYED, each already ranked. Never re-sorted here.
    return (
        <>
            {entry.rows.map((row) => (
                <tr key={row.key} className={styles.DrillDownRow}>
                    <td className={styles.DrillDownLabel} title={row.label}>
                        {row.label}
                    </td>
                    {columns.map((column) =>
                        // A breakup row that omits a column has nothing to say there — blank, unlike an FY row's N/A.
                        column.key in row.cells ? (
                            <td
                                key={column.key}
                                className={clsx(styles.Cell, cellBandClass(row.cells[column.key], column), {
                                    [styles.CellReference]: isDrillDownReferenceColumn(reconId, column.key),
                                })}
                            >
                                <ReconCellContent cell={row.cells[column.key]} column={column} unit={unit} />
                            </td>
                        ) : (
                            <td key={column.key} className={styles.Cell} />
                        ),
                    )}
                </tr>
            ))}
            {note && (
                <tr className={styles.DrillDownRow}>
                    <td colSpan={columns.length + 1} className={styles.DrillDownEmpty}>
                        {note}
                    </td>
                </tr>
            )}
        </>
    );
}

export default DrillDownRows;
